import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useParams } from 'react-router-dom';
import {
  Box, Card, CircularProgress, Collapse, Grid, IconButton, LinearProgress, MenuItem, Stack, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { ExpandLessRounded as ExpandLessIcon, ExpandMoreRounded as ExpandMoreIcon } from '@mui/icons-material';
import {
  BlockHeading, DataTable, DatagridEmpty, KpiTile, PageTitle, StatusDot, type StatusDotProps,
} from '@/components/shell';
import type { IOcrTestRun, IOcrTestRunResult } from '@/types';
import { OcrTestResultStatus } from '@/types';
import { ocrTestingService } from './ocrTestingService';
import { useOcrLabels } from './ocrLabels';
import { formatDuration, formatRunDate, passRate } from './runFormat';
import { FieldResultsTable } from './FieldResultsTable';
import { RunState } from './RunState';

const POLL_INTERVAL = 5000;
const BACK_TO = '/ocr-testing/runs';

type StatusFilter = '' | '2' | '3';

const RESULT_TONE: Record<OcrTestResultStatus, StatusDotProps['tone']> = {
  [OcrTestResultStatus.Passed]: 'ok',
  [OcrTestResultStatus.Failed]: 'problem',
  [OcrTestResultStatus.Error]: 'attention',
};

/** What a result unfolds into: its error, if any, and the field-by-field comparison. */
const ResultDetail: React.FC<{ result: IOcrTestRunResult }> = ({ result }) => {
  const t = useTranslate();
  return (
    <Stack spacing={1} sx={{ py: 1 }}>
      {result.errorMessage && (
        <Typography variant="body2" sx={{ color: 'error.dark' }}>{result.errorMessage}</Typography>
      )}
      {result.fieldResults && result.fieldResults.length > 0 ? (
        <FieldResultsTable results={result.fieldResults} />
      ) : (
        <Typography variant="body2" color="text.secondary">{t('screens.ocrTesting.noFieldDetail')}</Typography>
      )}
    </Stack>
  );
};

const ExpandButton: React.FC<{ open: boolean; onToggle: () => void }> = ({ open, onToggle }) => {
  const t = useTranslate();
  const label = t(open ? 'screens.ocrTesting.hideFields' : 'screens.ocrTesting.showFields');
  return (
    <Tooltip title={label}>
      <IconButton aria-label={label} aria-expanded={open} onClick={(e) => { e.stopPropagation(); onToggle(); }}>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </Tooltip>
  );
};

const ResultStatus: React.FC<{ result: IOcrTestRunResult }> = ({ result }) => {
  const labels = useOcrLabels();
  return <StatusDot tone={RESULT_TONE[result.status]} label={labels.resultStatus(result.status)} />;
};

/** One case's row, with its field comparison unfolding under it. */
const ResultRow: React.FC<{ result: IOcrTestRunResult }> = ({ result }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((o) => !o);

  return (
    <>
      <TableRow onClick={toggle} sx={{ cursor: 'pointer' }}>
        <TableCell padding="checkbox"><ExpandButton open={open} onToggle={toggle} /></TableCell>
        <TableCell>{result.testCaseName}</TableCell>
        <TableCell><ResultStatus result={result} /></TableCell>
        <TableCell align="right">{result.responseTimeMs}</TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={4}>
            <Collapse in appear timeout="auto"><ResultDetail result={result} /></Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

/** The phone form of a result row: a card that unfolds the same detail. */
const ResultCard: React.FC<{ result: IOcrTestRunResult }> = ({ result }) => {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((o) => !o);

  return (
    <Card sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }} onClick={toggle}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{result.testCaseName}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
            <ResultStatus result={result} />
            <Typography variant="body2" color="text.secondary">
              {t('screens.ocrTesting.timeValue', { ms: result.responseTimeMs })}
            </Typography>
          </Box>
        </Box>
        <ExpandButton open={open} onToggle={toggle} />
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit><ResultDetail result={result} /></Collapse>
    </Card>
  );
};

export const TestRunDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const t = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const [run, setRun] = useState<IOcrTestRun | null>(null);
  const [results, setResults] = useState<IOcrTestRunResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async (showLoader = true) => {
    if (!id) return;
    if (showLoader) setLoading(true);
    try {
      const [runRes, resultsRes] = await Promise.all([
        ocrTestingService.getRun(id),
        ocrTestingService.getRunResults(id),
      ]);
      setRun(runRes.data);
      setResults(Array.isArray(resultsRes.data) ? resultsRes.data : []);
      return runRes.data;
    } catch {
      notify('screens.ocrTesting.loadResultsFailed', { type: 'error' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    fetchData().then((runData) => {
      if (runData && !runData.finishedAt) {
        pollRef.current = setInterval(async () => {
          const updated = await fetchData(false);
          if (updated?.finishedAt && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, POLL_INTERVAL);
      }
    });
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label={t('ra.page.loading')} />
      </Box>
    );
  }

  if (!run) {
    return (
      <Stack spacing={2} sx={{ pt: 1.5 }}>
        <PageTitle title={t('screens.ocrTesting.runsTitle')} backTo={BACK_TO} />
        <Card><DatagridEmpty title={t('screens.ocrTesting.runNotFound')} hint={t('screens.ocrTesting.runNotFoundHint')} /></Card>
      </Stack>
    );
  }

  const isRunning = !run.finishedAt;
  const rate = passRate(run);
  const countOf = (status: OcrTestResultStatus) => results.filter((r) => r.status === status).length;
  const filteredResults = filterStatus ? results.filter((r) => r.status === Number(filterStatus)) : results;
  const noResults = <DatagridEmpty title={t('screens.ocrTesting.noResults')} hint={t('screens.ocrTesting.noResultsHint')} />;
  const subtitle = [
    run.sdkVersion ? t('screens.ocrTesting.sdkValue', { version: run.sdkVersion }) : null,
    t('screens.ocrTesting.durationValue', { value: formatDuration(run.startedAt, run.finishedAt) }),
  ].filter(Boolean).join(' · ');

  return (
    <Stack spacing={3} sx={{ pt: 1.5, pb: 3 }}>
      <PageTitle
        title={t('screens.ocrTesting.runTitle', { date: formatRunDate(run.startedAt) })}
        subtitle={subtitle}
        adornment={isRunning ? <RunState run={run} /> : undefined}
        backTo={BACK_TO}
      />

      {/* A Stack strips its children's margins, which Grid's negative gutter needs. */}
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <KpiTile
              label={t('screens.ocrTesting.passRate')}
              value={isRunning ? null : `${rate}%`}
              detail={t('screens.ocrTesting.passedOfTotal', { passed: run.passedCases, total: run.totalCases })}
            />
          </Grid>
          <Grid item xs={4} sm={3}>
            <KpiTile label={t('screens.ocrTesting.passed')} value={run.passedCases} />
          </Grid>
          <Grid item xs={4} sm={3}>
            <KpiTile label={t('screens.ocrTesting.failed')} value={run.failedCases} />
          </Grid>
          <Grid item xs={4} sm={3}>
            <KpiTile label={t('screens.ocrTesting.errors')} value={run.errorCases} />
          </Grid>
        </Grid>
        {isRunning && <LinearProgress sx={{ mt: 2 }} aria-label={t('screens.ocrTesting.running')} />}
      </Box>

      <Box component="section" aria-labelledby="run-results">
        <BlockHeading
          id="run-results"
          title={t('screens.ocrTesting.results')}
          action={(
            <TextField
              select
              size="small"
              label={t('screens.ocrTesting.show')}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as StatusFilter)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">{t('screens.ocrTesting.showAll', { count: results.length })}</MenuItem>
              <MenuItem value="2">{t('screens.ocrTesting.showFailed', { count: countOf(OcrTestResultStatus.Failed) })}</MenuItem>
              <MenuItem value="3">{t('screens.ocrTesting.showErrors', { count: countOf(OcrTestResultStatus.Error) })}</MenuItem>
            </TextField>
          )}
        />
        {isPhone ? (
          <Stack spacing={1}>
            {filteredResults.length === 0 && <Card>{noResults}</Card>}
            {filteredResults.map((result) => <ResultCard key={result.id} result={result} />)}
          </Stack>
        ) : (
          <DataTable
            label={t('screens.ocrTesting.results')}
            empty={filteredResults.length === 0 ? noResults : undefined}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" aria-label={t('screens.ocrTesting.showFields')} />
                <TableCell>{t('screens.ocrTesting.testCase')}</TableCell>
                <TableCell>{t('screens.ocrTesting.status')}</TableCell>
                <TableCell align="right">{t('screens.ocrTesting.timeMs')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredResults.map((result) => <ResultRow key={result.id} result={result} />)}
            </TableBody>
          </DataTable>
        )}
      </Box>
    </Stack>
  );
};
